// Reações de mensagem: cada pessoa tem no máximo UMA reação por mensagem —
// clicar em outro emoji troca, clicar no mesmo remove. A regra vale também no
// banco, pela chave única (message_id, user_auth_id) da tabela.

export interface ChatMessageReaction {
  id: string;
  message_id: string;
  user_auth_id: string;
  emoji: string;
  created_at?: string;
}

export interface ResumoReacao {
  emoji: string;
  total: number;
  souEu: boolean;
}

export const EMOJIS_DE_REACAO = ['👍', '❤', '😢', '😄', '😂', '🎉'] as const;

// Agrupa por emoji para virar as pílulas embaixo da mensagem. Mais reagido
// primeiro; empate mantém a ordem em que o emoji apareceu, pra fileira não
// ficar dançando a cada nova reação.
export function resumirReacoes(
  reacoes: ChatMessageReaction[],
  meuAuthId: string | null
): ResumoReacao[] {
  const porEmoji = new Map<string, ResumoReacao>();

  for (const reacao of reacoes) {
    const atual = porEmoji.get(reacao.emoji);
    if (atual) {
      atual.total += 1;
      atual.souEu = atual.souEu || reacao.user_auth_id === meuAuthId;
    } else {
      porEmoji.set(reacao.emoji, {
        emoji: reacao.emoji,
        total: 1,
        souEu: !!meuAuthId && reacao.user_auth_id === meuAuthId,
      });
    }
  }

  return [...porEmoji.values()].sort((a, b) => b.total - a.total);
}

// Resultado de clicar num emoji, calculado localmente para a contagem mudar na
// hora, sem esperar o servidor.
export function alternarMinhaReacao(
  reacoes: ChatMessageReaction[],
  meuAuthId: string,
  emoji: string
): ChatMessageReaction[] {
  const minhaAtual = reacoes.find(r => r.user_auth_id === meuAuthId);
  const semAMinha = reacoes.filter(r => r.user_auth_id !== meuAuthId);

  // Clicou no que já estava marcado: desfaz.
  if (minhaAtual?.emoji === emoji) return semAMinha;

  return [
    ...semAMinha,
    {
      // id provisório: quem manda é o banco, isto vive só até a próxima carga.
      id: `local-${meuAuthId}-${emoji}`,
      message_id: reacoes[0]?.message_id ?? '',
      user_auth_id: meuAuthId,
      emoji,
    },
  ];
}

// Texto de "quem reagiu" para o tooltip da pílula: sem isso a pílula mostra só
// o emoji e a contagem, e não dá pra saber quem curtiu. Eu apareço primeiro,
// como "Você"; quem já saiu da sala (e por isso não tem nome à mão) vira
// "alguém" em vez de sumir da conta.
export function descreverQuemReagiu(
  reacoes: ChatMessageReaction[],
  emoji: string,
  meuAuthId: string | null,
  nomePorAuthId: Record<string, string>
): string {
  const autores = reacoes.filter(r => r.emoji === emoji).map(r => r.user_auth_id);
  if (autores.length === 0) return '';

  const euReagi = !!meuAuthId && autores.includes(meuAuthId);
  const nomes = [
    ...(euReagi ? ['Você'] : []),
    ...autores.filter(id => id !== meuAuthId).map(id => nomePorAuthId[id] || 'alguém'),
  ];

  const lista =
    nomes.length === 1 ? nomes[0] : `${nomes.slice(0, -1).join(', ')} e ${nomes[nomes.length - 1]}`;

  return `${lista} ${nomes.length === 1 ? 'reagiu' : 'reagiram'} com ${emoji}`;
}
